import { type Infer, v } from 'convex/values'

import { HABIT_LEVELS, habitLevel } from './progression'

// Earned "finishes" for a habit's done dots: a metal over any dot style, free
// to earn by habit level (or a 100-day streak for Diamond), never taken away.

export const FINISH_IDS = ['bronze', 'silver', 'gold', 'platinum', 'diamond'] as const
export type FinishId = (typeof FINISH_IDS)[number]

export const finishIdValidator = v.union(
  v.literal('bronze'),
  v.literal('silver'),
  v.literal('gold'),
  v.literal('platinum'),
  v.literal('diamond'),
)
export const finishChoiceValidator = v.union(finishIdValidator, v.literal('none'))
export type FinishChoice = Infer<typeof finishChoiceValidator>

// Habit levels top out at 9 (Ancient), so the metals sit on the upper rungs.
export const FINISH_RULES: Record<FinishId, { level: number } | { streak: number }> = {
  bronze: { level: 5 },
  silver: { level: 6 },
  gold: { level: 7 },
  platinum: { level: 8 },
  diamond: { streak: 100 },
}

/** The finish a habit's done dots render with (null = none). */
export const finishOf = (h: { dotFinish?: FinishChoice }): FinishId | null =>
  h.dotFinish && h.dotFinish !== 'none' ? h.dotFinish : null

export const rankOf = (id: FinishId) => FINISH_IDS.indexOf(id)
export const isFinish = (x: unknown): x is FinishId => FINISH_IDS.includes(x as FinishId)

export type FinishFacts = { total: number; best: number }

export function finishEarned(id: FinishId, f: FinishFacts) {
  const r = FINISH_RULES[id]
  return 'level' in r ? habitLevel(f.total).level >= r.level : f.best >= r.streak
}

export const earnedFinishes = (f: FinishFacts) => FINISH_IDS.filter((id) => finishEarned(id, f))

export type FinishProgress =
  | { kind: 'level'; level: number; toGo: number }
  | { kind: 'streak'; have: number; need: number }

/** How far `f` is from a locked finish: check-ins to its level, or streak days. */
export function finishProgress(id: FinishId, f: FinishFacts): FinishProgress {
  const r = FINISH_RULES[id]
  if ('streak' in r) return { kind: 'streak', have: Math.min(f.best, r.streak), need: r.streak }
  const at = HABIT_LEVELS[r.level - 1].at
  return { kind: 'level', level: r.level, toGo: Math.max(0, at - f.total) }
}

/** The lowest-ranked finish not yet unlocked, or null once all are. */
export const nextFinish = (unlocked: readonly FinishId[]) =>
  FINISH_IDS.find((id) => !unlocked.includes(id)) ?? null

export const highest = (ids: readonly FinishId[]) =>
  ids.reduce<FinishId | null>((a, b) => (!a || rankOf(b) > rankOf(a) ? b : a), null)

export type FinishLook = {
  // CSS background (Diamond is conic; `flat` is a linear stand-in where conic
  // isn't supported: Satori, React Native).
  gradient: string
  flat: string
  shadow: string
  // Dark ink for an icon drawn on the metal; `tone` for text on dark, `text` on light.
  ink: string
  tone: string
  text: string
  sweep: 'slow' | 'normal' | 'spark'
}

export const FINISH_LOOK: Record<FinishId, FinishLook> = {
  bronze: {
    gradient: 'linear-gradient(135deg,#f3c08f 0%,#b5703a 45%,#7a4320 70%,#d99a62 100%)',
    flat: 'linear-gradient(135deg,#f3c08f 0%,#b5703a 45%,#7a4320 70%,#d99a62 100%)',
    shadow: 'inset 0 1px 0 rgba(255,255,255,0.35)',
    ink: '#3d2210',
    tone: '#e0a06a',
    text: '#9a5a2a',
    sweep: 'slow',
  },
  silver: {
    gradient: 'linear-gradient(135deg,#ffffff 0%,#b8bfc7 40%,#7d858e 65%,#e3e7ea 100%)',
    flat: 'linear-gradient(135deg,#ffffff 0%,#b8bfc7 40%,#7d858e 65%,#e3e7ea 100%)',
    shadow: 'inset 0 1px 0 rgba(255,255,255,0.6)',
    ink: '#2f353b',
    tone: '#d6dbe0',
    text: '#5f6770',
    sweep: 'slow',
  },
  gold: {
    gradient: 'linear-gradient(135deg,#fff2b0 0%,#f2c230 35%,#b8860b 62%,#ffe27a 100%)',
    flat: 'linear-gradient(135deg,#fff2b0 0%,#f2c230 35%,#b8860b 62%,#ffe27a 100%)',
    shadow: 'inset 0 1px 0 rgba(255,255,255,0.55), 0 0 8px rgba(246,201,69,0.35)',
    ink: '#5a3d00',
    tone: '#f6c945',
    text: '#9a6f00',
    sweep: 'normal',
  },
  platinum: {
    gradient: 'linear-gradient(135deg,#f4fbff 0%,#c7d9e6 30%,#d7c8f0 55%,#a9c9d9 75%,#f0f7fb 100%)',
    flat: 'linear-gradient(135deg,#f4fbff 0%,#c7d9e6 30%,#d7c8f0 55%,#a9c9d9 75%,#f0f7fb 100%)',
    shadow: 'inset 0 1px 0 rgba(255,255,255,0.7), 0 0 8px rgba(200,225,240,0.3)',
    ink: '#33475a',
    tone: '#c9e4f2',
    text: '#4f7590',
    sweep: 'normal',
  },
  diamond: {
    gradient:
      'conic-gradient(from 30deg,#e8fbff,#8fd8f5,#ffffff,#b4b0ff,#e8fbff,#7fe0ff,#ffffff,#e8fbff)',
    flat: 'linear-gradient(135deg,#e8fbff 0%,#8fd8f5 22%,#ffffff 40%,#b4b0ff 58%,#7fe0ff 78%,#ffffff 90%,#e8fbff 100%)',
    shadow: 'inset 0 0 0 1px rgba(255,255,255,0.5), 0 0 10px rgba(143,216,245,0.45)',
    ink: '#164a60',
    tone: '#9fe8ff',
    text: '#1f84ad',
    sweep: 'spark',
  },
}

// Cells smaller than this keep a static metal; rows animate at most this many.
export const FINISH_SHINE_MIN_PX = 12
export const FINISH_SHINE_MAX_CELLS = 60
// Seconds between neighbouring cells' sweeps.
export const FINISH_SHINE_STAGGER = 0.08
export const finishSweepSeconds = (id: FinishId) => (FINISH_LOOK[id].sweep === 'slow' ? 9 : 7)
